import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress,
  Snackbar, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AppShell from './components/AppShell';
import QuotaSection from './logic/QuotasSection';
import api from './services/api';

const ROWS_PER_PAGE = 25;

const BulkQuotas = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [sectionStates, setSectionStates] = useState({});
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      api
        .get('/alchemer/questions/', { params: { survey_id: res.data.alchemer_survey_id } })
        .then((qRes) => {
          setQuestions(qRes.data.data.map((q) => ({ id: q.id, title: q.title, options: q.options || [] })));
        });
    });
  }, [surveyId]);

  // Auto-expand first sheet when file is loaded
  useEffect(() => {
    if (sheets.length > 0) setExpandedSheet(sheets[0].name);
  }, [sheets]);

  const handleFile = async (file) => {
    if (!file.name.match(/\.xlsx$/i)) {
      setSnackbar({ severity: 'error', message: 'Only .xlsx files are supported.' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `/surveys/${surveyId}/parse-bulk-quotas/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSheets(data.sheets || []);
      setSectionStates({});
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: `Failed to parse file: ${err.response?.data?.error || err.message}`,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    const sections = sheets
      .map((sheet) => {
        const state = sectionStates[sheet.name] || {};
        return {
          namePrefix: sheet.name,
          logicTemplate: state.logicTemplate,
          quotaRows: state.quotaRows || sheet.rows.map((r) => ({ ...r, slotValues: {} })),
          description: state.description || '',
        };
      })
      .filter((s) => s.quotaRows?.length > 0);

    if (!sections.length) {
      setSnackbar({ severity: 'warning', message: 'No quota rows to submit.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/surveys/${surveyId}/create-nat-rep-quotas/`, {
        overallN: 0,
        sections,
      });
      const { created, errors } = res.data;
      if (errors.length) {
        setSnackbar({
          severity: 'warning',
          message: `Created ${created.length} quota(s). ${errors.length} error(s): ${errors[0]}`,
        });
      } else {
        setSnackbar({ severity: 'success', message: `Successfully created ${created.length} quota(s) in Alchemer.` });
      }
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: `Failed to submit: ${err.response?.data?.error || err.message}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey) return null;

  const totalRows = sheets.reduce((sum, s) => sum + s.rows.length, 0);

  return (
    <AppShell maxWidth="md">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/add-quotas`} style={{ textDecoration: 'none', color: 'inherit' }}>Alchemer quota setup</Link>
        <Typography color="textPrimary">Bulk Upload</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <TableChartRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Bulk Quota Upload
        </Typography>
      </Box>

      {/* Upload area */}
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragOver ? '#1976d2' : '#c0c0c0'}`,
          borderRadius: 3,
          p: 4,
          mb: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          transition: 'border-color 0.2s, background-color 0.2s',
          '&:hover': { borderColor: '#1976d2', bgcolor: 'primary.50' },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        {uploading ? (
          <CircularProgress size={32} />
        ) : (
          <>
            <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" fontWeight={600}>
              {sheets.length > 0
                ? 'Drop a new file to replace, or click to browse'
                : 'Drop your quota spreadsheet here, or click to browse'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Supports .xlsx — each sheet becomes a quota section
            </Typography>
            {sheets.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                {sheets.map((s) => (
                  <Chip key={s.name} label={`${s.name} (${s.rows.length})`} size="small" />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Sections — one Accordion per sheet, unmount on collapse so only active section renders */}
      {sheets.map((sheet) => (
        <Accordion
          key={sheet.name}
          expanded={expandedSheet === sheet.name}
          onChange={(_, open) => setExpandedSheet(open ? sheet.name : false)}
          elevation={0}
          sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', mb: 2, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600}>
                {sheet.name}
              </Typography>
              <Chip label={`${sheet.rows.length} rows`} size="small" color="primary" variant="outlined" />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }} TransitionProps={{ unmountOnExit: true }}>
            <QuotaSection
              title="Quota cells"
              initialRows={sheet.rows.map((r) => ({ ...r, slotValues: {} }))}
              value={sectionStates[sheet.name] || {}}
              onChange={(state) =>
                setSectionStates((prev) => ({ ...prev, [sheet.name]: state }))
              }
              questions={questions}
              overallN={0}
              useAbsoluteLimits
              showDescription
              rowsPerPage={ROWS_PER_PAGE}
            />
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Submit */}
      {sheets.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {submitting ? 'Submitting…' : `Submit ${totalRows} quota(s)`}
          </Button>
        </Box>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={7000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </AppShell>
  );
};

export default BulkQuotas;
